import { TemplateVariables } from 'crono-fe-common/types/template';
import { IVariable } from './model';
import {
  ITemplateSnippet,
  ITemplateVariables,
} from 'crono-fe-common/types/templateVariables';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { LanguageOption } from 'crono-fe-common/types/languageOption';
import EmailIcon from 'crono-fe-common/icons/Icon-Email';
import LinkedinInvitationIcon from 'crono-fe-common/icons/Icon-Linkedin-Invitation';
import LinkedinIconMessage from 'crono-fe-common/icons/Icon-Linkedin-Message';
import LinkedinIconInMailCompass from 'crono-fe-common/icons/Icon-Linkedin-Inmail-New';
import ScriptsIcon from 'crono-fe-common/icons/Icon-Scripts';
import LinkedinIconInvoice from 'crono-fe-common/icons/Icon-Linkedin-Invoice';
import { colors } from 'crono-fe-common/theme';
import { createHashedObject } from 'utils/uiHelpers';
import { mapLanguageToLabel } from 'utils/formatUtils';

export const templateVariables: IVariable[] = [
  {
    variable: TemplateVariables.PROSPECT_NAME,
    text: 'Contact Name',
  },
  {
    variable: TemplateVariables.PROSPECT_SURNAME,
    text: 'Contact Surname',
  },
  {
    variable: TemplateVariables.PROSPECT_TITLE,
    text: 'Contact Title',
  },
  {
    variable: TemplateVariables.COMPANY_NAME,
    text: 'Contact Company',
  },
  {
    variable: TemplateVariables.PROSPECT_LOCATION,
    text: 'Contact Location',
  },
  {
    variable: TemplateVariables.USER_NAME,
    text: 'User Name',
  },
  {
    variable: TemplateVariables.USER_SURNAME,
    text: 'User Surname',
  },
  {
    variable: TemplateVariables.USER_COMPANY_NAME,
    text: 'User Company',
  },
  {
    variable: TemplateVariables.PROSPECT_OWNER,
    text: 'Contact Owner',
  },
  {
    variable: TemplateVariables.COMPANY_OWNER,
    text: 'Company Owner',
  },
];

export const templateDefaultOption: {
  icon: JSX.Element;
  bgColorClass: string;
} = {
  icon: <EmailIcon color={colors.white} />,
  bgColorClass: 'bg-gray-400',
};

export const templateTypeOptions: {
  type: TemplateType;
  icon: JSX.Element;
  text: string;
  supportsAttachments?: boolean;
  supportsRichText?: boolean;
  supportsImage?: boolean;
  bgColorClass: string;
  shouldHideWithUpgradeBadges?: boolean;
  hasSubject?: boolean;
  hasTextContent?: boolean;
}[] = [
  {
    type: TemplateType.EMAIL,
    icon: <EmailIcon color={colors.white} />,
    text: 'Email',
    supportsAttachments: true,
    supportsRichText: true,
    supportsImage: true,
    bgColorClass: 'bg-red-500',
    shouldHideWithUpgradeBadges: true,
    hasSubject: true,
    hasTextContent: true,
  },
  {
    type: TemplateType.INVITATION,
    icon: <LinkedinInvitationIcon color={colors.white} />,
    text: 'Linkedin Invitation',
    supportsAttachments: false,
    bgColorClass: 'bg-blue-500',
    hasTextContent: true,
  },
  {
    type: TemplateType.LINKEDIN,
    icon: <LinkedinIconMessage color={colors.white} />,
    text: 'Linkedin Message',
    supportsAttachments: true,
    bgColorClass: 'bg-blue-500',
    hasTextContent: true,
  },
  {
    type: TemplateType.VOICE_NOTE,
    icon: <LinkedinIconInvoice size={16} color={colors.white} />,
    text: 'Linkedin Voice Note',
    supportsAttachments: false,
    bgColorClass: 'bg-blue-500',
  },
  {
    type: TemplateType.IN_MAIL,
    icon: <LinkedinIconInMailCompass color={colors.white} />,
    text: 'Linkedin InMail',
    supportsAttachments: true,
    bgColorClass: 'bg-purple-500',
    hasSubject: true,
    hasTextContent: true,
  },
  {
    type: TemplateType.SCRIPT,
    icon: <ScriptsIcon color={colors.white} />,
    text: 'Call Script',
    supportsAttachments: false,
    bgColorClass: 'bg-green-600',
    hasTextContent: true,
  },
];

export const hashedTemplateTypeOptions = createHashedObject(
  templateTypeOptions,
  'type',
);

export const templateExternalVariables: {
  variableKeyName: keyof ITemplateVariables;
  variableKeyValues: keyof ITemplateVariables;
  text?: string;
  isMultiple?: boolean;
  isSnippet?: boolean;
}[] = [
  {
    variableKeyName: 'productNameTemplateVariable',
    variableKeyValues: 'productName',
    text: 'Product name',
  },
  {
    variableKeyName: 'productDescriptionTemplateVariable',
    variableKeyValues: 'productDescription',
    text: 'Product description',
  },
  {
    variableKeyName: 'productReferencesTemplateVariables',
    variableKeyValues: 'productReferences',
    text: 'Customer reference',
    isMultiple: true,
  },
  {
    variableKeyName: 'productBenefitsTemplateVariables',
    variableKeyValues: 'productBenefits',
    text: 'Product benefit',
    isMultiple: true,
  },
  {
    variableKeyName: 'productIndustriesTemplateVariables',
    variableKeyValues: 'productIndustries',
    text: 'Industry challenge',
    isMultiple: true,
  },
  {
    variableKeyName: 'templateSnippets',
    variableKeyValues: 'templateSnippets',
    isMultiple: true,
    isSnippet: true,
  },
];

export const createVariablesFromTemplateVariables = (
  templateVariables: ITemplateVariables | undefined,
): IVariable[] => {
  if (!templateVariables) return [];

  const nonEmpty = templateExternalVariables.filter((variable) => {
    const values = templateVariables[variable.variableKeyName];
    return Array.isArray(values) && !!values.length;
  });

  return nonEmpty
    .map((variable) => {
      const values = templateVariables[variable.variableKeyName];

      if (variable.isMultiple && Array.isArray(values)) {
        if (variable.isSnippet) {
          return (values as ITemplateSnippet[]).map((value) => {
            return {
              variable: value.templateVariableName,
              text: value.label,
              userBased: value.userBased,
              content: value.content,
            };
          });
        }

        return values.map((value, i) => {
          return {
            variable: value,
            text: `${variable.text} ${i + 1}`,
          };
        });
      }

      return {
        variable: templateVariables[variable.variableKeyName],
        text: variable.text,
      };
    })
    .flat() as IVariable[];
};

export interface BuildGenerateTemplatePromptArgs {
  templateType: TemplateType;
  language?: string | null;
  title?: string | null;
  subject?: string | null;
  productDescription?: string | null;
  targetAudience?: string | null;
}

export const buildGenerateTemplatePrompt = ({
  templateType,
  language,
  title,
  subject,
}: BuildGenerateTemplatePromptArgs): string => {
  const typeLabel = hashedTemplateTypeOptions[templateType]?.text ?? '';
  const article = /^[aeiou]/i.test(typeLabel) ? 'an' : 'a';
  const parts: string[] = [`Write ${article} ${typeLabel} template`];

  if (language)
    parts.push(`in ${mapLanguageToLabel(language as LanguageOption)}`);
  if (title) parts.push(`named ${title}`);
  if (subject) parts.push(`with the subject ${subject}`);

  return parts.join(' ') + '. The goal of the template is';
};
